import { useState } from "react";

const industries = [
  {
    id: "professional",
    tag: "01",
    icon: "ri-briefcase-4-line",
    title: "Professional Services",
    description:
      "Structured systems for firms that require precision, compliance, and reliability in every client interaction.",
    color: "#4ADE80",
    image:
      "https://public.readdy.ai/ai/img_res/5cad38294f49d31b276240f13fd4ec40.jpg",
    useCases: [
      { icon: "ri-user-add-line", label: "Client intake automation" },
      { icon: "ri-git-branch-line", label: "Case & workflow tracking" },
      { icon: "ri-calendar-check-line", label: "Appointment scheduling" },
      { icon: "ri-file-transfer-line", label: "Document coordination" },
    ],
    stat: { value: "3x", label: "Faster client onboarding" },
  },
  {
    id: "contractor",
    tag: "02",
    icon: "ri-tools-line",
    title: "Contractor & Home Services",
    description:
      "Ensure every lead is captured, scheduled, and followed up—without gaps or missed opportunities.",
    color: "#4ADE80",
    image:
      "https://readdy.ai/api/search-image?query=professional%20contractor%20crew%20reviewing%20project%20blueprint%20on%20a%20dark%20moody%20construction%20site%2C%20structured%20planning%20board%20with%20job%20schedules%20and%20tools%2C%20industrial%20lighting%20cinematic%20editorial%20photography%20premium%20dark%20tones%20minimal%20background&width=900&height=600&seq=ind002&orientation=landscape",
    useCases: [
      { icon: "ri-phone-line", label: "Missed call text-back" },
      { icon: "ri-calendar-schedule-line", label: "Job scheduling automation" },
      { icon: "ri-route-line", label: "Lead routing" },
      { icon: "ri-send-plane-line", label: "Estimate follow-ups" },
    ],
    stat: { value: "0", label: "Missed leads slipping through" },
  },
  {
    id: "realestate",
    tag: "03",
    icon: "ri-building-2-line",
    title: "Real Estate & Brokerage",
    description:
      "Turn every inquiry into a managed opportunity with structured follow-up systems that convert.",
    color: "#4ADE80",
    image:
      "https://readdy.ai/api/search-image?query=luxury%20real%20estate%20office%20interior%20with%20modern%20minimal%20dark%20aesthetic%2C%20agent%20reviewing%20property%20listings%20on%20screens%2C%20structured%20pipeline%20dashboard%20display%2C%20cinematic%20dark%20mood%20editorial%20premium%20photography%20architectural%20setting&width=900&height=600&seq=ind003&orientation=landscape",
    useCases: [
      { icon: "ri-filter-line", label: "Lead capture & nurture" },
      { icon: "ri-calendar-2-line", label: "Appointment booking" },
      { icon: "ri-bar-chart-box-line", label: "Pipeline tracking" },
      { icon: "ri-message-3-line", label: "Client communication automation" },
    ],
    stat: { value: "5x", label: "More leads converted to meetings" },
  },
];

export default function IndustryCards() {
  const [active, setActive] = useState<string>("professional");

  const activeIndustry = industries.find((i) => i.id === active)!;

  return (
    <section className="bg-[#09090B] py-32 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p
            className="text-xs uppercase tracking-widest text-[#4ADE80] font-semibold mb-3"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Industry Solutions
          </p>
          <h2
            className="text-4xl md:text-5xl font-extrabold text-white"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Built for Your Business, <br />
            <span className="text-white/40">Not a Generic Template</span>
          </h2>
        </div>

        {/* Tab Selector */}
        <div className="flex items-center justify-center gap-2 mb-14 bg-white/5 border border-white/10 rounded-full px-1 py-1 w-fit mx-auto">
          {industries.map((ind) => (
            <button
              key={ind.id}
              onClick={() => setActive(ind.id)}
              className={`whitespace-nowrap px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer ${
                active === ind.id
                  ? "bg-[#4ADE80] text-[#09090B]"
                  : "text-white/50 hover:text-white"
              }`}
            >
              {ind.title}
            </button>
          ))}
        </div>

        {/* Content Panel */}
        <div
          key={activeIndustry.id}
          className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-white/8"
        >
          {/* Left — Image */}
          <div className="relative h-80 lg:h-auto min-h-[420px]">
            <img
              src={activeIndustry.image}
              alt={activeIndustry.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#09090B]/80 lg:block hidden" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#09090B]/80 to-transparent lg:hidden" />

            {/* Stat badge */}
            <div className="absolute bottom-6 left-6 bg-[#09090B]/90 border border-[#4ADE80]/30 rounded-xl px-5 py-3">
              <p
                className="text-2xl font-extrabold text-[#4ADE80]"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {activeIndustry.stat.value}
              </p>
              <p className="text-xs text-white/50 mt-0.5">{activeIndustry.stat.label}</p>
            </div>
          </div>

          {/* Right — Content */}
          <div className="bg-[#0F0F12] p-10 lg:p-14 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#4ADE80]/10 border border-[#4ADE80]/20">
                <i className={`${activeIndustry.icon} text-[#4ADE80] text-lg`} />
              </div>
              <span
                className="text-xs font-semibold text-white/30 uppercase tracking-widest"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {activeIndustry.tag} / {activeIndustry.title}
              </span>
            </div>

            <h3
              className="text-3xl font-extrabold text-white mb-4 leading-snug"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {activeIndustry.title}
            </h3>

            <p
              className="text-white/50 text-base leading-relaxed mb-8"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {activeIndustry.description}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {activeIndustry.useCases.map((uc) => (
                <div
                  key={uc.label}
                  className="flex items-center gap-3 bg-white/4 border border-white/8 rounded-lg px-4 py-3"
                >
                  <div className="w-7 h-7 flex items-center justify-center shrink-0">
                    <i className={`${uc.icon} text-[#4ADE80] text-base`} />
                  </div>
                  <span
                    className="text-sm text-white/70 font-medium"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {uc.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
